import { useIntersectionObserver } from "usehooks-ts";
import { TIndexableBlock } from "../../../types/general-block-types";
import { TImage } from "../../../types/image-types";
import { TLink } from "../../../types/link-types";
import { Heading, LinkWrapper } from "../../elements";
import HeroFullWidthImage from "../../elements/hero-full-width-image/hero-full-width-image";
import HeroFullWidthVideo from "../../elements/hero-full-width-video/hero-full-width-video";
import styles from "./hero-media-block.module.less";
import animations from "../../../animation/animation.module.less";

export type THeroMediaBlockProps = {
  eyebrow?: string;
  title: string;
  text?: string;
  link?: TLink;
  mediaType?: "image" | "video";
  imageDesktop?: TImage;
  imageMobile?: TImage;
  videoUrlDesktop?: string;
  videoUrlMobile?: string;
} & TIndexableBlock;

const HeroMediaBlock = ({
  eyebrow,
  title,
  text,
  link,
  mediaType = "image",
  imageDesktop,
  imageMobile,
  videoUrlDesktop,
  videoUrlMobile,
  index,
}: THeroMediaBlockProps) => {
  const isNotFirstBlock = typeof index === "number" && index > 0;

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.25,
    freezeOnceVisible: true,
  });

  const animationClassesText = [
    index === 0 ? animations.fadeInDelay100 : isIntersecting ? animations.fadeInDelay100 : "",
    animations.opacity0,
  ].join(" ");

  return (
    <section ref={ref} className={"max-width padded-sides centered space-mb-2xl"}>
      <div className={styles.innerWrapper}>
        <div className={[styles.textWrapper, animationClassesText].join(" ")}>
          <div className={styles.readabilityBackdrop} />
          {eyebrow && <p className={[styles.eyebrow, "eyebrow"].join(" ")}>{eyebrow}</p>}
          <Heading className={styles.title} visualType={"h1"} semanticType={isNotFirstBlock ? "h2" : "h1"}>
            {title}
          </Heading>
          {(text || link) && (
            <p className={[styles.text, "body-l mb-0"].join(" ")}>
              {text}{" "}
              {link && (
                <LinkWrapper className={styles.link} url={link.url} onPreNavigate={link.onPreNavigate}>
                  {link.title}
                </LinkWrapper>
              )}
            </p>
          )}
        </div>
        {mediaType === "video" && videoUrlDesktop && (
          <HeroFullWidthVideo
            blockIndex={index}
            enablePauseButton={true}
            videoUrlMobile={videoUrlMobile}
            videoUrlDesktop={videoUrlDesktop ?? ""}
          />
        )}
        {mediaType === "image" && (imageDesktop || imageMobile) && (
          <HeroFullWidthImage blockIndex={index} imageDesktop={imageDesktop} imageMobile={imageMobile} />
        )}
      </div>
    </section>
  );
};

export default HeroMediaBlock;
